import type { ComponentType } from "react";
import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import { ContentHeader } from "../components/ContentHeader";
import { EntityCard } from "../components/EntityCard";
import { NavBackend, NavSurfaces, NavIntegrations, NavClients, StatRunbooks } from "../components/Icons";
import type { AnyEntity, BackendOrSurfaceEntity, IntegrationEntity, ClientEntity } from "../api/types";

type IconC = ComponentType<{ size?: number; className?: string }>;
const SectionLabel = ({ icon: Icon, children }: { icon: IconC; children: React.ReactNode }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <Icon size={15} />{children}
  </span>
);

export function SearchResults() {
  const ui = useUI();
  const data = useData();
  const q = ui.searchQuery.trim().toLowerCase();

  if (!q) return <div className="empty">Type something to search.</div>;

  const filterEntities = <T extends AnyEntity>(items: T[]): T[] => {
    return items.filter((it) => {
      const hay = [
        it.name,
        "notes" in it ? it.notes : "",
        it.owner_team,
        it.slack,
        "type" in it ? it.type : "",
        "kind" in it ? it.kind : "",
        ...((it as BackendOrSurfaceEntity).dependencies ?? []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  };

  const groups: Array<{ id: string; label: string; icon: IconC; items: AnyEntity[] }> = [
    { id: "backend",      label: "Backend",      icon: NavBackend,      items: filterEntities<BackendOrSurfaceEntity>(data.commandCenter?.estates.backend ?? []) },
    { id: "surfaces",     label: "Surfaces",     icon: NavSurfaces,     items: filterEntities<BackendOrSurfaceEntity>(data.commandCenter?.estates.surfaces ?? []) },
    { id: "integrations", label: "Integrations", icon: NavIntegrations, items: filterEntities<IntegrationEntity>(data.commandCenter?.estates.integrations ?? []) },
    { id: "clients",      label: "Clients",      icon: NavClients,      items: filterEntities<ClientEntity>(data.commandCenter?.estates.clients ?? []) },
  ];

  const runbookHits = (data.commandCenter?.runbooks ?? []).filter((rb) => {
    const hay = [rb.title, rb.summary, rb.service, rb.path].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const total = groups.reduce((s, g) => s + g.items.length, 0) + runbookHits.length;

  if (total === 0) {
    return (
      <>
        <ContentHeader title={`Search: "${q}"`} description="Matches across services, integrations, clients, and runbooks." />
        <div className="empty">No matches.</div>
      </>
    );
  }

  return (
    <>
      <ContentHeader title={`Search: "${q}"`} count={total} description="Matches across services, integrations, clients, and runbooks." />
      {groups.map((g) => g.items.length > 0 && (
        <section key={g.id}>
          <div className="section-header"><h2><SectionLabel icon={g.icon}>{g.label}</SectionLabel></h2><span className="count">{g.items.length}</span></div>
          <div className="grid">{g.items.map((it) => <EntityCard key={it.id} item={it} />)}</div>
        </section>
      ))}
      {runbookHits.length > 0 && (
        <section>
          <div className="section-header"><h2><SectionLabel icon={StatRunbooks}>Runbooks</SectionLabel></h2><span className="count">{runbookHits.length}</span></div>
          <ul className="list">
            {runbookHits.map((rb) => (
              <li key={rb.path}>
                <a href={`https://github.com/WaffyApp/WaffyMonitoring/blob/main/${rb.path}`}
                   target="_blank" rel="noopener">{rb.title}</a>
                {rb.service && <span style={{ color: "var(--text-muted)" }}> — {rb.service}</span>}
                {rb.summary && (
                  <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                    {rb.summary.length > 180 ? rb.summary.slice(0, 179) + "…" : rb.summary}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
